"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.factoryNotationFor = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2020 GoodData Corporation
var flow_1 = tslib_1.__importDefault(require("lodash/flow"));
var identity_1 = tslib_1.__importDefault(require("lodash/identity"));
var isArray_1 = tslib_1.__importDefault(require("lodash/isArray"));
var isObject_1 = tslib_1.__importDefault(require("lodash/isObject"));
var isString_1 = tslib_1.__importDefault(require("lodash/isString"));
var stringify_object_1 = tslib_1.__importDefault(require("stringify-object"));
var objRef_1 = require("../../objRef");
var sort_1 = require("../base/sort");
var filter_1 = require("../filter");
var measure_1 = require("../measure");
var attribute_1 = require("../attribute");
var stringify = function (input) {
    return stringify_object_1.default(input, {
        singleQuotes: false,
        inlineCharacterLimit: 50,
        indent: "    ",
    });
};
var ARRAY_JOINER = ", ";
var getObjQualifierValue = function (value) { return (objRef_1.isUriRef(value) ? value.uri : value.identifier); };
// dot suffix handling e. g. ".localIdentifier(...)"
// is curried explicitly to allow easier composition in cases where more than one dot suffix is supported
var addStringBuilderSegment = function (identifier, helperName) {
    if (helperName === void 0) { helperName = identifier; }
    return function (objToConvert) {
        return function (value) {
            return objToConvert[identifier] ? value + "." + helperName + "(\"" + objToConvert[identifier] + "\")" : value;
        };
    };
};
var addAggregation = addStringBuilderSegment("aggregation");
var addAlias = addStringBuilderSegment("alias");
var addFormat = addStringBuilderSegment("format");
var addLocalId = addStringBuilderSegment("localIdentifier", "localId");
var addTitle = addStringBuilderSegment("title");
var addFilters = function (_a) {
    var filters = _a.filters;
    return function (value) {
        return filters ? value + ".filters(" + filters.map(exports.factoryNotationFor).join(ARRAY_JOINER) + ")" : value;
    };
};
var addRatio = function (_a) {
    var computeRatio = _a.computeRatio;
    return function (value) {
        return computeRatio ? value + ".ratio()" : value;
    };
};
var getBuilder = function (defaultBuilder, builderSegmentHandlers) {
    var builder = flow_1.default(builderSegmentHandlers)(defaultBuilder);
    return builder === defaultBuilder ? "undefined" : builder;
};
// converters for each supported object to Model notation string
var convertAttribute = function (_a) {
    var attribute = _a.attribute;
    var builder = getBuilder("a => a", [addAlias(attribute), addLocalId(attribute)]);
    return "newAttribute(" + stringify(attribute.displayForm) + ", " + builder + ")";
};
var baseMeasureDotAdders = function (measure) { return [
    addAlias(measure),
    addFormat(measure),
    addLocalId(measure),
    addTitle(measure),
]; };
var convertSimpleMeasure = function (measure, definition) {
    var builder = getBuilder("m => m", tslib_1.__spreadArrays(baseMeasureDotAdders(measure), [
        addAggregation(definition.measureDefinition),
        addFilters(definition.measureDefinition),
        addRatio(definition.measureDefinition),
    ]));
    return "newMeasure(" + stringify(definition.measureDefinition.item) + ", " + builder + ")";
};
var convertArithmeticMeasure = function (measure, definition) {
    var builder = getBuilder("m => m", baseMeasureDotAdders(measure));
    return "newArithmeticMeasure(" + stringify(definition.arithmeticMeasure.measureIdentifiers) + ", \"" + definition.arithmeticMeasure.operator + "\", " + builder + ")";
};
var convertPopMeasure = function (measure, definition) {
    var builder = getBuilder("m => m", baseMeasureDotAdders(measure));
    return "newPopMeasure(\"" + definition.popMeasureDefinition.measureIdentifier + "\", \"" + getObjQualifierValue(definition.popMeasureDefinition.popAttribute) + "\", " + builder + ")";
};
var convertPreviousPeriodMeasure = function (measure, definition) {
    var builder = getBuilder("m => m", baseMeasureDotAdders(measure));
    return "newPreviousPeriodMeasure(\"" + definition.previousPeriodMeasure.measureIdentifier + "\", [" + (definition.previousPeriodMeasure.dateDataSets &&
        definition.previousPeriodMeasure.dateDataSets
            .map(function (s) {
            return stringify({
                dataSet: getObjQualifierValue(s.dataSet),
                periodsAgo: s.periodsAgo,
            });
        })
            .join(ARRAY_JOINER)) + "], " + builder + ")";
};
var convertMeasure = function (_a) {
    var measure = _a.measure;
    var definition = measure.definition;
    if (measure_1.isMeasureDefinition(definition)) {
        return convertSimpleMeasure(measure, definition);
    }
    else if (measure_1.isArithmeticMeasureDefinition(definition)) {
        return convertArithmeticMeasure(measure, definition);
    }
    else if (measure_1.isPoPMeasureDefinition(definition)) {
        return convertPopMeasure(measure, definition);
    }
    else if (measure_1.isPreviousPeriodMeasureDefinition(definition)) {
        return convertPreviousPeriodMeasure(measure, definition);
    }
    throw new Error("Unknown measure type");
};
var convertAttributeAreaSortItem = function (_a) {
    var attributeSortItem = _a.attributeSortItem;
    return "newAttributeAreaSort(\"" + attributeSortItem.attributeIdentifier + "\", \"" + attributeSortItem.direction + "\", \"" + attributeSortItem.aggregation + "\")";
};
var convertAttributeSortItem = function (_a) {
    var attributeSortItem = _a.attributeSortItem;
    return "newAttributeSort(\"" + attributeSortItem.attributeIdentifier + "\", \"" + attributeSortItem.direction + "\", " + !!attributeSortItem.aggregation + ")";
};
var convertMeasureSortItem = function (_a) {
    var measureSortItem = _a.measureSortItem;
    var locators = measureSortItem.locators || [];
    var measureLocator = locators.find(function (l) { return sort_1.isMeasureLocator(l); });
    var attributeLocators = locators.filter(function (l) { return !sort_1.isMeasureLocator(l); });
    return "newMeasureSort(\"" + measureLocator.measureLocatorItem.measureIdentifier + "\", \"" + measureSortItem.direction + "\", " + stringify(attributeLocators) + ")";
};
var convertAbsoluteDateFilter = function (_a) {
    var _b = _a.absoluteDateFilter, dataSet = _b.dataSet, from = _b.from, to = _b.to;
    var args = [dataSet, from, to].filter(identity_1.default).map(stringify);
    return "newAbsoluteDateFilter(" + args.join(ARRAY_JOINER) + ")";
};
var convertRelativeDateFilter = function (_a) {
    var _b = _a.relativeDateFilter, dataSet = _b.dataSet, granularity = _b.granularity, from = _b.from, to = _b.to;
    var args = [dataSet, granularity, from, to].filter(identity_1.default).map(stringify);
    return "newRelativeDateFilter(" + args.join(ARRAY_JOINER) + ")";
};
var convertPositiveAttributeFilter = function (_a) {
    var _b = _a.positiveAttributeFilter, displayForm = _b.displayForm, inValues = _b.in;
    var args = [displayForm, inValues].filter(identity_1.default).map(stringify);
    return "newPositiveAttributeFilter(" + args.join(ARRAY_JOINER) + ")";
};
var convertNegativeAttributeFilter = function (_a) {
    var _b = _a.negativeAttributeFilter, displayForm = _b.displayForm, notIn = _b.notIn;
    var args = [displayForm, notIn].filter(identity_1.default).map(stringify);
    return "newNegativeAttributeFilter(" + args.join(ARRAY_JOINER) + ")";
};
var convertMeasureValueFilter = function (_a) {
    var _b = _a.measureValueFilter, measure = _b.measure, condition = _b.condition;
    var ref = stringify(measure);
    if (filter_1.isComparisonCondition(condition)) {
        return "newMeasureValueFilter(" + ref + ", \"" + condition.comparison.operator + "\", " + condition.comparison.value + ")";
    }
    else if (filter_1.isRangeCondition(condition)) {
        return "newMeasureValueFilter(" + ref + ", \"" + condition.range.operator + "\", " + condition.range.from + ", " + condition.range.to + ")";
    }
    return "{ measureValueFilter: { measure: " + ref + " }";
};
var convertRankingFilter = function (_a) {
    var _b = _a.rankingFilter, measure = _b.measure, attributes = _b.attributes, value = _b.value, operator = _b.operator;
    var attributesString = attributes === null || attributes === void 0 ? void 0 : attributes.map(stringify).join(ARRAY_JOINER);
    var args = [
        stringify(measure),
        attributesString && "[" + attributesString + "]",
        "\"" + operator + "\"",
        "" + value,
    ].filter(isString_1.default);
    return "newRankingFilter(" + args.join(ARRAY_JOINER) + ")";
};
/**
 * Returns a code for generating the provided input using convenience factory methods where possible.
 * @param data - data to return the generating code for
 * @public
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
exports.factoryNotationFor = function (data) {
    if (isArray_1.default(data)) {
        return "[" + data.map(exports.factoryNotationFor).join(ARRAY_JOINER) + "]";
    }
    else if (attribute_1.isAttribute(data)) {
        return convertAttribute(data);
    }
    else if (measure_1.isMeasure(data)) {
        return convertMeasure(data);
    }
    else if (sort_1.isAttributeAreaSort(data)) {
        return convertAttributeAreaSortItem(data);
    }
    else if (sort_1.isAttributeSort(data)) {
        return convertAttributeSortItem(data);
    }
    else if (sort_1.isMeasureSort(data)) {
        return convertMeasureSortItem(data);
    }
    else if (filter_1.isAbsoluteDateFilter(data)) {
        return convertAbsoluteDateFilter(data);
    }
    else if (filter_1.isRelativeDateFilter(data)) {
        return convertRelativeDateFilter(data);
    }
    else if (filter_1.isPositiveAttributeFilter(data)) {
        return convertPositiveAttributeFilter(data);
    }
    else if (filter_1.isNegativeAttributeFilter(data)) {
        return convertNegativeAttributeFilter(data);
    }
    else if (filter_1.isMeasureValueFilter(data)) {
        return convertMeasureValueFilter(data);
    }
    else if (filter_1.isRankingFilter(data)) {
        return convertRankingFilter(data);
    }
    return isObject_1.default(data) || isString_1.default(data) ? stringify(data) : data;
};
//# sourceMappingURL=index.js.map